import React, { useState, useContext, useCallback } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import * as Yup from 'yup';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  DialogActions,
} from '@material-ui/core';
import { Formik } from 'formik';

import fetch from 'utils/fetch';
import { ResearchSheetState } from 'enums';
import { ResearchSheet as ResearchSheetType } from 'types';

import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { getResearchSheet } from './research-sheet-api';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    link: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    dialog: {
      width: 350,
    },
    dialogTitle: {
      fontWeight: 'bold',
    },

    dialogActions: {
      marginTop: 20,
      padding: '8px 24px',
    },
  })
);

async function requestExtension(
  itemId: string,
  expirationDate: string
): Promise<{ ok: boolean; data: any }> {
  try {
    await fetch(
      `/api/research-sheet/${itemId}/request-extension?expirationDate=${expirationDate}`,
      {
        method: 'PUT',
      }
    );

    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    console.log(e);

    return {
      ok: false,
      data: e,
    };
  }
}

export function ExtensionCellComponent({
  data,
  allData,
}: {
  data: any;
  allData: any;
}) {
  const classes = useStyles();
  const { showNotification } = useContext(NotificationCtx);
  const [openedDialog, setOpenedDialog] = useState<boolean>(false);
  const [row, setRow] = useState<ResearchSheetType>(allData);

  const { id, requestedExtensionTo, state, expiration } = row;
  const isActive = state === ResearchSheetState.ACTIVE;
  const hasExtension = requestedExtensionTo != null;

  const handleOpenDialog = () => setOpenedDialog(true);
  const handleCloseDialog = () => setOpenedDialog(false);

  const handleRequestExtension = useCallback(
    async (values: { extensionDate: string }) => {
      const response = await requestExtension(id, values.extensionDate);

      if (response.ok) {
        showNotification(
          'Žádost o prodloužení platnosti BL byla úspěšně odeslána',
          NotificationVariant.SUCCESS
        );

        // refreshing current row
        const sheetWithExtension = await getResearchSheet(id);
        setRow(sheetWithExtension!);

        handleCloseDialog();
      } else {
        showNotification(
          `Žádost o prodloužení platnosti BL se nepodařilo odeslat.
        
      Chyba: ${response.data?.message ?? '-'}`,
          NotificationVariant.ERROR
        );
      }
    },
    [id, showNotification]
  );

  const futureDate = expiration ? new Date(expiration) : new Date();
  futureDate.setDate(futureDate.getDate() + 30);

  const extendedExpiration = requestedExtensionTo
    ? requestedExtensionTo
    : futureDate.toISOString().substring(0, 10);

  return hasExtension ? (
    <Typography component="span">
      {new Date(extendedExpiration).toLocaleDateString('cs')}
    </Typography>
  ) : (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleOpenDialog}
        disabled={!isActive}
      >
        Prodloužit
      </Button>

      <Dialog open={openedDialog}>
        <Grid className={classes.dialog}>
          <Formik
            initialValues={{ extensionDate: extendedExpiration }}
            onSubmit={handleRequestExtension}
            validationSchema={Yup.object().shape({
              extensionDate: Yup.string().test('extensionDate', '', function (
                value
              ) {
                const { createError, path } = this;

                if (value != null && expiration != null) {
                  if (new Date(expiration) > new Date(value)) {
                    return createError({
                      path,
                      message:
                        'Datum prodloužení musí být větší než původní datum expirace.',
                    });
                  }
                }
                return true;
              }),
            })}
          >
            {({ handleSubmit, errors }) => (
              <>
                <DialogTitle>
                  <Typography component="span" className={classes.dialogTitle}>
                    Žádost o prodloužení platnosti BL
                  </Typography>
                </DialogTitle>
                <DialogContent>
                  <FormItemTextField
                    name="extensionDate"
                    label="Prodloužit do"
                    type="date"
                    disabled={false}
                  />
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <Button
                    color="primary"
                    onClick={() => handleSubmit()}
                    disabled={!!errors.extensionDate}
                  >
                    Potvrdit
                  </Button>
                  <Button onClick={handleCloseDialog}>Zavřít</Button>
                </DialogActions>
              </>
            )}
          </Formik>
        </Grid>
      </Dialog>
    </>
  );
}
